import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiRequestError } from '../api/client';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../lib/useToast';

/** Cambio de la propia contraseña. Se muestra obligatoriamente cuando la clave fue asignada por Gerencia (temporal). */
export function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { show } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (next !== confirm) return setError(t('password.mismatch'));
    if (next === current) return setError(t('password.same'));
    setBusy(true);
    try {
      await api.changePassword(current, next);
      // El servidor cierra todas las sesiones del usuario: se vuelve a ingresar con la clave nueva.
      await logout().catch(() => undefined);
      show(t('password.done'));
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) setError(t(`errors.${err.code}`, { ...err.params, defaultValue: t('errors.UNKNOWN', { code: err.code }) }));
      else setError(t('errors.UNKNOWN', { code: 'CLIENT' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate" id="gate">
      <div className="gatecard">
        <div className="gatelogo">
          <img src="/teo-logo.png" alt="TEO Inventory Control" />
          <h1>{t('password.title')}</h1>
          <p>{user?.full_name}</p>
        </div>
        <form className="loginform" id="pwForm" onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="field">
            <label htmlFor="pwCurrent">{t('password.current')}</label>
            <input id="pwCurrent" className="input" type="password" autoComplete="current-password" autoFocus value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pwNew">{t('password.new')}</label>
            <input id="pwNew" className="input" type="password" autoComplete="new-password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pwConfirm">{t('password.confirm')}</label>
            <input id="pwConfirm" className="input" type="password" autoComplete="new-password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <button type="submit" className="btn loginbtn" id="pwSubmit" disabled={busy || !current || next.length < 8 || !confirm}>
            {t('password.submit')}
          </button>
        </form>
        {error ? (
          <div className="help loginnotice loginerror" role="alert">
            {error}
          </div>
        ) : null}
        {user?.must_change_password ? (
          <div className="help loginnotice" id="pwForced" role="status">
            {t('password.forced')}
          </div>
        ) : null}
        <div className="loginfoot">
          <button type="button" className="btn" id="pwCancel" onClick={() => void logout().then(() => navigate('/login', { replace: true }))}>
            {t('password.cancel')}
          </button>
          <LanguageSwitch />
        </div>
      </div>
    </div>
  );
}
